import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface RateLimitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rateLimit: number;
}

const LANDING_URL = import.meta.env.VITE_LANDING_URL || 'https://vivosign.com';
const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.vivosign.com';

const RateLimitDialog: React.FC<RateLimitDialogProps> = ({
  open,
  onOpenChange,
  rateLimit,
}) => {
  const { t } = useTranslation('quicksend');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <DialogTitle className="text-lg">
              {t('rate_limit_title')}
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-gray-600 leading-relaxed">
            {t('rate_limit_description', { limit: rateLimit })}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
          <p className="font-medium mb-1">{t('rate_limit_upgrade_title')}</p>
          <p className="text-blue-700">{t('rate_limit_upgrade_description')}</p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            className="w-full"
            onClick={() => {
              window.location.href = `${APP_URL}/join`;
            }}
          >
            {t('rate_limit_create_account')}
          </Button>
          <a
            href={`${LANDING_URL}/fair-use`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-center text-gray-500 hover:text-gray-700 underline"
          >
            {t('rate_limit_policy_link')}
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RateLimitDialog;
